export const addColumnFilter = (columnId, filter) => ({ type: 'add', columnId, filter });
export const removeColumnFilter = (columnId, filter) => ({ type: 'remove', columnId, filter });
export const clearColumnFilter = (columnId) => ({ type: 'clear', columnId });
export const clearAllColumnFilters = () => ({ type: 'clearAll' });
/** Replaces every term of one column (an edit of the column's filter rather than an append). */
export const replaceColumnFilters = (columnId, filters) => ({ type: 'replace', columnId, filters });
export function sameColumnFilter(a, b) {
    return (a.mode === b.mode &&
        Boolean(a.exact) === Boolean(b.exact) &&
        a.value.trim().toLowerCase() === b.value.trim().toLowerCase());
}
export function columnFilterReducer(state, action) {
    if (action.type === 'clearAll')
        return Object.keys(state).length ? {} : state;
    const terms = state[action.columnId] ?? [];
    switch (action.type) {
        case 'add': {
            const value = action.filter.value.trim();
            if (!value || terms.some((term) => sameColumnFilter(term, action.filter)))
                return state;
            return { ...state, [action.columnId]: [...terms, { ...action.filter, value }] };
        }
        case 'remove': {
            const remaining = terms.filter((term) => !sameColumnFilter(term, action.filter));
            if (remaining.length === terms.length)
                return state;
            if (remaining.length)
                return { ...state, [action.columnId]: remaining };
            return columnFilterReducer(state, clearColumnFilter(action.columnId));
        }
        case 'clear': {
            if (!(action.columnId in state))
                return state;
            const next = { ...state };
            delete next[action.columnId];
            return next;
        }
        case 'replace': {
            const next = action.filters
                .map((term) => ({ ...term, value: term.value.trim() }))
                .filter((term, index, all) => term.value && all.findIndex((other) => sameColumnFilter(other, term)) === index);
            if (!next.length)
                return columnFilterReducer(state, clearColumnFilter(action.columnId));
            return { ...state, [action.columnId]: next };
        }
    }
}
export const COLUMN_FILTER_OPERATOR_LABELS = {
    contains: 'contains',
    notContains: 'does not contain',
    equals: 'equals',
    notEquals: 'does not equal',
};
/** The operators for a column's supported matches, each match followed by its negation. */
export function columnFilterOperators(matches) {
    return matches.flatMap((match) => match === 'equals' ? ['equals', 'notEquals'] : ['contains', 'notContains']);
}
export function columnFilterOperator({ mode, exact }) {
    if (exact)
        return mode === 'include' ? 'equals' : 'notEquals';
    return mode === 'include' ? 'contains' : 'notContains';
}
/** The term an operator applies to `value`. */
export function columnFilterFromOperator(operator, value) {
    const mode = operator === 'contains' || operator === 'equals' ? 'include' : 'exclude';
    return operator === 'equals' || operator === 'notEquals' ? { mode, value, exact: true } : { mode, value };
}
/** How a term reads in labels: its operator, e.g. "does not contain". */
export function columnFilterVerb(filter) {
    return COLUMN_FILTER_OPERATOR_LABELS[columnFilterOperator(filter)];
}
/** Flattens the non-blank terms in `columnOrder`, labelled for display as removable chips. */
export function getActiveColumnFilters(filters, columnOrder, columnLabels, formatValue = (_, value) => value) {
    return columnOrder.flatMap((columnId) => (filters[columnId] ?? []).flatMap(({ mode, value, exact }) => {
        const trimmed = value.trim();
        const verb = columnFilterVerb({ mode, exact });
        return trimmed
            ? [
                {
                    columnId,
                    mode,
                    value,
                    ...(exact ? { exact } : {}),
                    key: JSON.stringify(exact ? [columnId, mode, value, 'exact'] : [columnId, mode, value]),
                    label: `${columnLabels[columnId]} ${verb} ${formatValue(columnId, trimmed)}`,
                },
            ]
            : [];
    }));
}
/** Columns with at least one term, for the grid's controlled header indicator. */
export function filteredColumnIds(filters) {
    return new Set(Object.entries(filters)
        .filter(([, terms]) => terms !== undefined && terms.length > 0)
        .map(([columnId]) => columnId));
}
/**
 * Matches a row for consumers that filter in memory. `readText` returns the text a user filters
 * against in that column, usually its display value. Matching ignores case: a term matches any
 * substring, or the whole text when `exact`. Includes within a column use OR, columns use AND, and
 * any matching exclude rejects the row.
 */
export function matchesColumnFilters(filters, readText) {
    for (const [columnId, terms = []] of Object.entries(filters)) {
        const text = readText(columnId).toLowerCase();
        let hasIncludes = false;
        let matchesInclude = false;
        for (const term of terms) {
            const value = term.value.trim().toLowerCase();
            if (!value)
                continue;
            const matches = term.exact ? text === value : text.includes(value);
            if (term.mode === 'exclude') {
                if (matches)
                    return false;
            }
            else {
                hasIncludes = true;
                matchesInclude ||= matches;
            }
        }
        if (hasIncludes && !matchesInclude)
            return false;
    }
    return true;
}
//# sourceMappingURL=columnFilters.js.map